/**
 * Geographic limits for every /cities1 clipart skyline.
 *
 * Boxes are OpenStreetMap extents from Nominatim (October 2026), stored in
 * clipartBounds.json. A device point inside a box is inside that place.
 * When several places contain the point, the smallest box is the one shown.
 */

import skyline7 from "../../cities1/skyline7.json";
import clipartBounds from "../../cities1/clipartBounds.json";
import { getCareStartCityPage } from "./careStartCityCatalog";

/** @typedef {{ south: number, north: number, west: number, east: number }} CityBounds */

/** @type {Record<string, CityBounds>} */
export const CARESTART_CITY_BOUNDS = clipartBounds;

/**
 * @param {CityBounds} bounds
 * @returns {number}
 */
export function careStartCityBoundsAreaKm2(bounds) {
  const lat = ((bounds.south + bounds.north) / 2) * (Math.PI / 180);
  const heightKm = Math.abs(bounds.north - bounds.south) * 111.32;
  const widthKm = Math.abs(bounds.east - bounds.west) * 111.32 * Math.cos(lat);
  return heightKm * widthKm;
}

/**
 * @param {number} lat
 * @param {number} lng
 * @param {CityBounds} bounds
 * @returns {boolean}
 */
export function pointInCityBounds(lat, lng, bounds) {
  return lat >= bounds.south && lat <= bounds.north && lng >= bounds.west && lng <= bounds.east;
}

/**
 * Clipart city whose limits contain this point, or null.
 * @param {number} lat
 * @param {number} lng
 * @returns {{ cityName: string, src: string, slug: string, key: string }|null}
 */
export function findCareStartCityAtPoint(lat, lng) {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  let bestKey = null;
  let bestArea = Infinity;
  for (const [key, bounds] of Object.entries(CARESTART_CITY_BOUNDS)) {
    const fileSlug = skyline7[key];
    if (!fileSlug || !pointInCityBounds(lat, lng, bounds)) continue;
    const area = careStartCityBoundsAreaKm2(bounds);
    if (area < bestArea) {
      bestKey = key;
      bestArea = area;
    }
  }
  if (!bestKey) return null;
  const cityName = bestKey.slice(bestKey.indexOf("|") + 1);
  const slug = skyline7[bestKey];
  return {
    cityName,
    slug,
    key: bestKey,
    src: `/cities1/clipart7/${slug}.svg`,
  };
}

/**
 * CareMap2 city path (/caremap2/nairobi). Centers the map and shows that skyline.
 * @param {string} slug
 * @returns {{ key: string, cityName: string, slug: string, src: string, lat: number, lng: number }|null}
 */
export function careMapCityFromSlug(slug) {
  const city = getCareStartCityPage(slug);
  if (!city) return null;
  const key = `${city.country}|${city.cityName}`;
  const bounds = CARESTART_CITY_BOUNDS[key];
  if (!bounds) return null;
  return {
    key,
    cityName: city.cityName,
    slug: city.slug,
    src: city.src,
    lat: (bounds.south + bounds.north) / 2,
    lng: (bounds.west + bounds.east) / 2,
  };
}

export function listBoundedCareStartCities() {
  return Object.keys(CARESTART_CITY_BOUNDS)
    .filter((key) => skyline7[key])
    .map((key) => {
      const splitAt = key.indexOf("|");
      return {
        key,
        country: key.slice(0, splitAt),
        cityName: key.slice(splitAt + 1),
      };
    })
    .sort(
      (a, b) =>
        a.cityName.localeCompare(b.cityName) ||
        a.country.localeCompare(b.country),
    );
}
